import React from 'react';
import {
	Modal,
	ModalContent,
	ModalHeader,
	ModalBody,
	ModalFooter,
	Button,
	Input,
	Select,
	SelectItem,
	Checkbox,
	Divider,
	addToast
} from '@heroui/react';
import { Plane, Info, Trash2, Plus } from 'lucide-react';
import BaseApi from '../../api/BaseApi.js';
import ResourcesApi from '../../api/resourcesApi.js';
import AircraftModelSearch, { AircraftModelCard } from './components/AircraftModelSearch.js';

// Get org unique from URL
const getOrgUnique = () => {
	const match = window.location.pathname.match(/\/org\/([^/]+)\//);
	return match ? match[1] : '';
};

function AddAircraftModal({
	isOpen,
	onClose,
	locations,
	payStatuses,
	onSuccess
}) {
	const orgUnique = getOrgUnique();

	// Dropdown data from Symfony API (not SSR)
	const [pilotRatings, setPilotRatings] = React.useState([]);
	const [instructorRatings, setInstructorRatings] = React.useState([]);
	const [medicalClass, setMedicalClass] = React.useState([]);

	// Selected model state
	const [selectedModel, setSelectedModel] = React.useState(null);

	// Form state
	const [formData, setFormData] = React.useState({
		name: '',
		regNumb: '',
		pilotRatingID: '',
		instructorRatingID: '',
		medicalCertificate: '',
		endorsements: [],
		resourceLocationID: '',
		resourcePayStatus: '',
		resourceRate: '',
		enableHobbs: false,
		enableTachometers: false,
		// Hobbs counter
		hobbsValue: '',
		hobbsEnableRate: false,
		hobbsRate: ''
	});

	// Dynamic engine counters state
	const [engineCounters, setEngineCounters] = React.useState([]);

	const [formErrors, setFormErrors] = React.useState({});
	const [saving, setSaving] = React.useState(false);

	// Fetch dropdown data from Symfony API when modal opens
	React.useEffect(() => {
		if (!isOpen) return;
		const fetchDropdownData = async () => {
			try {
				const response = await ResourcesApi.getResourcesDropdownData();
				const data = response.data?.data || response.data || {};
				setPilotRatings(data.pilotRatings || []);
				setInstructorRatings(data.instructorRatings || []);
				setMedicalClass(data.medicalClass || data.medicalCertificates || []);
			} catch (err) {
				console.error('Failed to load dropdown data:', err);
			}
		};
		fetchDropdownData();
	}, [isOpen]);

	// Reset form when modal closes
	React.useEffect(() => {
		if (!isOpen) {
			setSelectedModel(null);
			setFormData({
				name: '',
				regNumb: '',
				pilotRatingID: '',
				instructorRatingID: '',
				medicalCertificate: '',
				endorsements: [],
				resourceLocationID: '',
				resourcePayStatus: '',
				resourceRate: '',
				enableHobbs: false,
				enableTachometers: false,
				hobbsValue: '',
				hobbsEnableRate: false,
				hobbsRate: ''
			});
			setEngineCounters([]);
			setFormErrors({});
		}
	}, [isOpen]);

	// Select an aircraft model (called from AircraftModelSearch)
	const handleSelectModel = model => {
		setSelectedModel(model);
		// Auto-fill name based on model
		const autoName = model.popularName
			? `${model.manufacturer} ${model.popularName}`
			: `${model.manufacturer} ${model.model}`;
		setFormData(prev => ({ ...prev, name: autoName }));

		// Initialize engine counters based on model's number of engines
		const numEngines = parseInt(model.numberEngine, 10) || 0;
		const initialEngines = [];
		for (let i = 0; i < numEngines; i++) {
			initialEngines.push({
				name: `Engine ${i + 1}`,
				value: '',
				enableRate: false,
				rate: ''
			});
		}
		setEngineCounters(initialEngines);
	};

	// Clear selected model to search again
	const handleClearModel = () => {
		setSelectedModel(null);
	};

	// Handle form input changes
	const handleInputChange = (field, value) => {
		setFormData(prev => ({ ...prev, [field]: value }));
		if (formErrors[field]) {
			setFormErrors(prev => ({ ...prev, [field]: null }));
		}
	};

	// Handle endorsements change
	const handleEndorsementsChange = values => {
		setFormData(prev => ({ ...prev, endorsements: values }));
	};

	// Handle engine counter changes
	const handleEngineChange = (index, field, value) => {
		setEngineCounters(prev => {
			const updated = [...prev];
			updated[index] = { ...updated[index], [field]: value };
			return updated;
		});
	};

	// Add new engine counter
	const handleAddEngine = () => {
		setEngineCounters(prev => [
			...prev,
			{
				name: `Engine ${prev.length + 1}`,
				value: '',
				enableRate: false,
				rate: ''
			}
		]);
	};

	// Remove engine counter
	const handleRemoveEngine = index => {
		setEngineCounters(prev => {
			const updated = prev.filter((_, i) => i !== index);
			// Rename remaining engines
			return updated.map((engine, i) => ({
				...engine,
				name: `Engine ${i + 1}`
			}));
		});
	};

	// Validate form
	const validateForm = () => {
		const errors = {};

		if (!selectedModel) {
			errors.model = 'Please select an aircraft model';
		}
		if (!formData.name.trim()) {
			errors.name = 'Aircraft name is required';
		}
		if (!formData.regNumb.trim()) {
			errors.regNumb = 'Registration number is required';
		}
		if (!formData.pilotRatingID) {
			errors.pilotRatingID = 'Pilot rating is required';
		}
		if (!formData.instructorRatingID) {
			errors.instructorRatingID = 'Instructor rating is required';
		}
		if (!formData.resourceLocationID) {
			errors.resourceLocationID = 'Location is required';
		}

		setFormErrors(errors);
		return Object.keys(errors).length === 0;
	};

	// Submit form
	const handleSubmit = async () => {
		if (!validateForm()) return;

		setSaving(true);
		try {
			// Build counters array from state
			const counters = [];

			// Add tachometer counters from engineCounters state
			engineCounters.forEach(engine => {
				counters.push({
					name: engine.name,
					value: String(engine.value || '0'),
					type: 'tachometer',
					enableRate: engine.enableRate,
					rate: engine.rate ? String(engine.rate) : null
				});
			});

			// Add hobbs counter if enabled
			if (formData.enableHobbs) {
				counters.push({
					name: 'Hobbs',
					value: String(formData.hobbsValue || '0'),
					type: 'hobbs',
					enableRate: formData.hobbsEnableRate,
					rate: formData.hobbsRate ? String(formData.hobbsRate) : null
				});
			}

			// Build endorsements object
			const endorsements = {
				complex: formData.endorsements.includes('complex'),
				performance: formData.endorsements.includes('performance'),
				tailwheel: formData.endorsements.includes('tailwheel'),
				highAltitudeOperations: formData.endorsements.includes('highAltitudeOperations')
			};

			// Parse medical certificate
			const medicalCert = formData.medicalCertificate;
			const isDrivingLicense = medicalCert === 'drivingLicense';
			const isBasicMed = medicalCert === 'basicMed';
			const medicalCertificateId = (!isDrivingLicense && !isBasicMed && medicalCert)
				? parseInt(medicalCert, 10)
				: null;

			const aircraftData = {
				// Required fields
				locationId: parseInt(formData.resourceLocationID, 10),
				name: formData.name,
				registrationNumber: formData.regNumb,
				modelId: selectedModel.id,
				pilotRatingId: parseInt(formData.pilotRatingID, 10),
				instructorRatingId: parseInt(formData.instructorRatingID, 10),
				numberOfEngines: parseInt(selectedModel.numberEngine, 10) || 0,
				// Endorsements as object
				endorsements,
				// Counters
				counters: counters.length > 0 ? counters : [],
				// Meters
				enableTachometers: formData.enableTachometers,
				enableHobbs: formData.enableHobbs
			};

			// Add optional fields only if they have values
			if (isDrivingLicense) {
				aircraftData.drivingLicense = true;
			}
			if (isBasicMed) {
				aircraftData.basicMedStatus = true;
			}
			if (medicalCertificateId) {
				aircraftData.medicalCertificateId = medicalCertificateId;
			}
			if (formData.resourcePayStatus) {
				aircraftData.payStatus = formData.resourcePayStatus;
			}
			if (formData.resourceRate) {
				aircraftData.rate = formData.resourceRate;
			}

			const response = await BaseApi.post(`/org/${orgUnique}/aircraft`, aircraftData);
			const result = response.data;

			// Show notifications from API response
			const { info = [], error: errorArr = [], warning = [], message = [], status = [] } = result || {};

			// Show info messages as success
			if (info.length > 0) {
				info.forEach(msg => {
					addToast({
						title: 'Success',
						description: msg,
						color: 'success'
					});
				});
			}

			// Show error messages
			if (errorArr.length > 0) {
				errorArr.forEach(msg => {
					addToast({
						title: 'Error',
						description: msg,
						color: 'danger'
					});
				});
			}

			// Show warning messages
			if (warning.length > 0) {
				warning.forEach(msg => {
					addToast({
						title: 'Warning',
						description: msg,
						color: 'warning'
					});
				});
			}

			// Show error messages from message or status arrays (fallback)
			if (message.length > 0) {
				message.forEach(msg => {
					addToast({
						title: 'Error',
						description: msg,
						color: 'danger'
					});
				});
			}

			if (status.length > 0 && typeof status[0] === 'string') {
				status.forEach(msg => {
					addToast({
						title: 'Error',
						description: msg,
						color: 'danger'
					});
				});
			}

			// Only close modal and call onSuccess if successful (has info and no errors)
			if ((info.length > 0 || result.success !== false) && errorArr.length === 0 && message.length === 0) {
				if (onSuccess) {
					onSuccess(result.data);
				}
				onClose();
			}
		} catch (error) {
			console.error('Error adding aircraft:', error);

			// Check if error response has structured error messages from API
			const errorData = error.response?.data;
			if (errorData) {
				const { error: errorArray = [], message = [], warning = [] } = errorData;

				// Show error messages from API
				if (errorArray.length > 0) {
					errorArray.forEach(msg => {
						addToast({
							title: 'Error',
							description: msg,
							color: 'danger'
						});
					});
					return;
				}

				// Show message array errors
				if (message.length > 0) {
					message.forEach(msg => {
						addToast({
							title: 'Error',
							description: msg,
							color: 'danger'
						});
					});
					return;
				}

				// Show warnings
				if (warning.length > 0) {
					warning.forEach(msg => {
						addToast({
							title: 'Warning',
							description: msg,
							color: 'warning'
						});
					});
					return;
				}
			}

			// Fallback to generic error message
			addToast({
				title: 'Error',
				description: error.message || 'Failed to add aircraft',
				color: 'danger'
			});
		} finally {
			setSaving(false);
		}
	};

	// Show rate input only for certain pay statuses
	const showRateInput = formData.resourcePayStatus === 'peruse' || formData.resourcePayStatus === 'perblock';

	return (
		<Modal isOpen={isOpen} onClose={onClose} size="3xl" scrollBehavior="inside">
			<ModalContent>
				{onCloseModal => (
					<>
						<ModalHeader className="flex flex-col gap-1">Add New Aircraft to Resources</ModalHeader>
						<ModalBody>
							<div className="space-y-6">
								{/* Section 1: Search Model - full width */}
								<div className="grid grid-cols-1 md:grid-cols-2 gap-6">
									{/* Search/Select Model */}
									<div className="space-y-3">
										<h4 className="font-semibold text-default-700">Find Aircraft Model</h4>

										{!selectedModel ? (
											<>
												<AircraftModelSearch
													onSelect={handleSelectModel}
													showHistory
													size="sm"
												/>
												<p className="text-xs text-danger">
													Don't see your aircraft model? Contact support to add it.
												</p>
											</>
										) : (
											<>
												<AircraftModelCard
													model={selectedModel}
													onChangeClick={handleClearModel}
												/>
												{formErrors.model && (
													<p className="text-xs text-danger">{formErrors.model}</p>
												)}
											</>
										)}
									</div>

									{/* Aircraft Name & Registration */}
									<div className="space-y-3">
										<h4 className="font-semibold text-default-700">Aircraft Details</h4>
										<Input
											label="Aircraft Name"
											placeholder="Enter aircraft name"
											value={formData.name}
											onValueChange={value => handleInputChange('name', value)}
											isInvalid={!!formErrors.name}
											errorMessage={formErrors.name}
											isRequired
											size="sm"
										/>
										<Input
											label="Registration Number"
											placeholder="e.g., N12345"
											value={formData.regNumb}
											onValueChange={value => handleInputChange('regNumb', value)}
											isInvalid={!!formErrors.regNumb}
											errorMessage={formErrors.regNumb}
											isRequired
											size="sm"
										/>
									</div>
								</div>

								<Divider />

								{/* Section 2: Requirements & Aircraft Meters - 2 columns */}
								<div className="grid grid-cols-1 md:grid-cols-2 gap-6">
									{/* Left: Requirements & Endorsements */}
									<div className="space-y-4">
										<div className="space-y-3">
											<h4 className="font-semibold text-default-700">Requirements</h4>
											<Select
												key={`pilot-${pilotRatings.length}`}
												label="Pilot Rating Required"
												placeholder="Select pilot rating"
												selectedKeys={formData.pilotRatingID ? [formData.pilotRatingID] : []}
												onSelectionChange={keys => handleInputChange('pilotRatingID', Array.from(keys)[0])}
												isInvalid={!!formErrors.pilotRatingID}
												errorMessage={formErrors.pilotRatingID}
												isRequired
												size="sm"
											>
												{(pilotRatings || []).map(rating => (
													<SelectItem key={String(rating.id)}>{rating.name}</SelectItem>
												))}
											</Select>

											<Select
												key={`instructor-${instructorRatings.length}`}
												label="Instructor Rating Required"
												placeholder="Select instructor rating"
												selectedKeys={formData.instructorRatingID ? [formData.instructorRatingID] : []}
												onSelectionChange={keys =>
													handleInputChange('instructorRatingID', Array.from(keys)[0])
												}
												isInvalid={!!formErrors.instructorRatingID}
												errorMessage={formErrors.instructorRatingID}
												isRequired
												size="sm"
											>
												{(instructorRatings || []).map(rating => (
													<SelectItem key={String(rating.id)}>{rating.name}</SelectItem>
												))}
											</Select>

											<Select
												label="Medical Certificate Required"
												placeholder="None"
												selectedKeys={formData.medicalCertificate ? [formData.medicalCertificate] : []}
												onSelectionChange={keys =>
													handleInputChange('medicalCertificate', Array.from(keys)[0] || '')
												}
												size="sm"
											>
												<SelectItem key="">None</SelectItem>
												<SelectItem key="drivingLicense">Driver's License</SelectItem>
												<SelectItem key="basicMed">Basic Med</SelectItem>
												{(medicalClass || []).map(cls => (
													<SelectItem key={String(cls.id)}>{cls.name}</SelectItem>
												))}
											</Select>
										</div>

										{/* Endorsements */}
										<div className="space-y-3">
											<h4 className="font-semibold text-default-700">Endorsements</h4>
											<div className="grid grid-cols-2 gap-3">
												<Checkbox
													isSelected={formData.endorsements.includes('complex')}
													onValueChange={checked => {
														const newEndorsements = checked
															? [...formData.endorsements, 'complex']
															: formData.endorsements.filter(e => e !== 'complex');
														handleEndorsementsChange(newEndorsements);
													}}
													size="sm"
												>
													Complex
												</Checkbox>
												<Checkbox
													isSelected={formData.endorsements.includes('performance')}
													onValueChange={checked => {
														const newEndorsements = checked
															? [...formData.endorsements, 'performance']
															: formData.endorsements.filter(e => e !== 'performance');
														handleEndorsementsChange(newEndorsements);
													}}
													size="sm"
												>
													High Performance
												</Checkbox>
												<Checkbox
													isSelected={formData.endorsements.includes('tailwheel')}
													onValueChange={checked => {
														const newEndorsements = checked
															? [...formData.endorsements, 'tailwheel']
															: formData.endorsements.filter(e => e !== 'tailwheel');
														handleEndorsementsChange(newEndorsements);
													}}
													size="sm"
												>
													Tailwheel
												</Checkbox>
												<Checkbox
													isSelected={formData.endorsements.includes('highAltitudeOperations')}
													onValueChange={checked => {
														const newEndorsements = checked
															? [...formData.endorsements, 'highAltitudeOperations']
															: formData.endorsements.filter(e => e !== 'highAltitudeOperations');
														handleEndorsementsChange(newEndorsements);
													}}
													size="sm"
												>
													High Altitude
												</Checkbox>
											</div>
										</div>
									</div>

									{/* Right: Aircraft Meters */}
									<div className="space-y-3">
										<h4 className="font-semibold text-default-700">Aircraft Meters</h4>

										{/* Hobbs Section */}
										<div className="space-y-3 bg-default-50 p-3 rounded-lg">
											<div className="flex items-center gap-4">
												<Checkbox
													isSelected={formData.enableHobbs}
													onValueChange={value => handleInputChange('enableHobbs', value)}
													isDisabled={!selectedModel}
													size="sm"
												>
													Enable Hobbs
												</Checkbox>
												{selectedModel && formData.enableHobbs && (
													<Input
														placeholder="0"
														value={formData.hobbsValue}
														onValueChange={value => handleInputChange('hobbsValue', value)}
														type="text"
														size="sm"
														className="w-24"
														classNames={{ input: 'text-right' }}
													/>
												)}
											</div>
											{selectedModel && formData.enableHobbs && formData.resourcePayStatus !== 'peruse' && (
												<div className="flex gap-3 pl-6">
													<div className="flex items-center h-8">
														<Checkbox
															isSelected={formData.hobbsEnableRate}
															onValueChange={value => handleInputChange('hobbsEnableRate', value)}
															size="sm"
														>
															Rate ($)/Hr
														</Checkbox>
													</div>
													<div className="flex items-center h-8">
														<Input
															placeholder="0.00"
															value={formData.hobbsRate}
															onValueChange={value => handleInputChange('hobbsRate', value)}
															type="text"
															size="sm"
															className={`w-24 ${!formData.hobbsEnableRate ? 'opacity-0' : ''}`}
															startContent="$"
															classNames={{
																input: 'text-right',
																inputWrapper: 'h-8 min-h-8'
															}}
														/>
													</div>
												</div>
											)}
										</div>

										{/* Tachometers/Engines Section */}
										<div className="space-y-3 bg-default-50 p-3 rounded-lg">
											<div className="flex items-center justify-between">
												<Checkbox
													isSelected={formData.enableTachometers}
													onValueChange={value => handleInputChange('enableTachometers', value)}
													isDisabled={!selectedModel}
													size="sm"
												>
													Enable Tachometers
												</Checkbox>
												{selectedModel && (
													<Button
														size="sm"
														variant="flat"
														color="success"
														startContent={<Plus className="w-3 h-3" />}
														onPress={handleAddEngine}
													>
														Add Engine
													</Button>
												)}
											</div>

											{/* Engine List */}
											{selectedModel && engineCounters.length > 0 && (
												<div className="space-y-3 mt-1">
													{engineCounters.map((engine, index) => (
														<div key={index} className="space-y-2 pl-2 border-l-2 border-default-200">
															<div className="flex items-center gap-2">
																<Button
																	isIconOnly
																	size="sm"
																	variant="light"
																	color="danger"
																	onPress={() => handleRemoveEngine(index)}
																>
																	<Trash2 className="w-3 h-3" />
																</Button>
																<span className="text-sm font-medium">{engine.name}</span>
																<Input
																	placeholder="0"
																	value={engine.value}
																	onValueChange={value => handleEngineChange(index, 'value', value)}
																	type="text"
																	size="sm"
																	className="w-24"
																	classNames={{ input: 'text-right' }}
																/>
															</div>
															{formData.resourcePayStatus !== 'peruse' && (
																<div className="flex gap-3 pl-8">
																	<div className="flex items-center h-8">
																		<Checkbox
																			isSelected={engine.enableRate}
																			onValueChange={value => handleEngineChange(index, 'enableRate', value)}
																			size="sm"
																		>
																			Rate ($)/Hr
																		</Checkbox>
																	</div>
																	<div className="flex items-center h-8">
																		<Input
																			placeholder="0.00"
																			value={engine.rate}
																			onValueChange={value => handleEngineChange(index, 'rate', value)}
																			type="text"
																			size="sm"
																			className={`w-24 ${!engine.enableRate ? 'opacity-0' : ''}`}
																			startContent="$"
																			classNames={{
																				input: 'text-right',
																				inputWrapper: 'h-8 min-h-8'
																			}}
																		/>
																	</div>
																</div>
															)}
														</div>
													))}
												</div>
											)}

											{selectedModel && engineCounters.length === 0 && (
												<p className="text-xs text-default-400 pl-6">No engines defined</p>
											)}
										</div>
									</div>
								</div>

								<Divider />

								{/* Section 3: Location & Billing - 2 columns */}
								<div className="grid grid-cols-1 md:grid-cols-3 gap-4">
									<Select
										label="Location"
										placeholder="Select location"
										selectedKeys={formData.resourceLocationID ? [formData.resourceLocationID] : []}
										onSelectionChange={keys =>
											handleInputChange('resourceLocationID', Array.from(keys)[0])
										}
										isInvalid={!!formErrors.resourceLocationID}
										errorMessage={formErrors.resourceLocationID}
										isRequired
										size="sm"
									>
										{(locations || []).map(loc => (
											<SelectItem key={String(loc.id)}>
												{loc.city ? `${loc.name}, ${loc.city}` : loc.name}
											</SelectItem>
										))}
									</Select>

									<Select
										label="Charge Type"
										placeholder="Not Applicable"
										selectedKeys={formData.resourcePayStatus ? [formData.resourcePayStatus] : []}
										onSelectionChange={keys =>
											handleInputChange('resourcePayStatus', Array.from(keys)[0] || '')
										}
										size="sm"
									>
										<SelectItem key="">Not Applicable</SelectItem>
										{(payStatuses || []).map(status => (
											<SelectItem key={status.value}>{status.name}</SelectItem>
										))}
									</Select>

									{showRateInput && (
										<Input
											label="Rate"
											placeholder="0.00"
											value={formData.resourceRate}
											onValueChange={value => handleInputChange('resourceRate', value)}
											startContent="$"
											type="number"
											size="sm"
										/>
									)}
								</div>
							</div>
						</ModalBody>
						<ModalFooter>
							<Button color="danger" variant="light" onPress={onCloseModal}>
								Cancel
							</Button>
							<Button
								color="primary"
								onPress={handleSubmit}
								isLoading={saving}
								isDisabled={!selectedModel}
								startContent={<Plane className="w-4 h-4" />}
							>
								Add Aircraft to Resources
							</Button>
						</ModalFooter>
					</>
				)}
			</ModalContent>
		</Modal>
	);
}

export default AddAircraftModal;
